/**
 * Your own account: who you are, and how the app treats you.
 *
 * This used to edit the workspace settings — `senderName`, `myEmail`,
 * `myPhone` — which are one set of values for everybody who opens the
 * workspace. An Agent correcting their own phone number changed the number
 * every screen showed, and the heading greeted them by the owner's name.
 *
 * It edits the signed-in person's own record now. Four fields are theirs to
 * change; the address they sign in with, their role and their department
 * are somebody else's decision, so they are shown as facts rather than as
 * empty boxes that would not save.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import Icon from '../../lib/icons.jsx';
import { initials, avatarClass } from '../../lib/format.js';
import { TIMEZONES } from '../../constants/defaults.js';
import { Section, Grid, TextField, SelectField, CheckGrid, Toggle } from './SettingsBits.jsx';

/** One fact about the account, shown rather than offered for editing. */
const Fact = ({ label, children, hint }) => (
  <div className="prof-fact">
    <span>{label}</span>
    <b>{children || '—'}</b>
    {hint ? <small>{hint}</small> : null}
  </div>
);

export default function ProfilePane() {
  const { S, me, toast, fmtDate } = useApp();
  const owner = !me || me.role === 'Owner';

  /* Seeded from the account and edited locally, so typing is not a request
     per keystroke. Re-seeded if /auth/me answers after this mounted. */
  const [form, setForm] = useState({ name: '', title: '', dept: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!me) return;
    setForm({ name: me.name || '', title: me.title || '', dept: me.dept || '', phone: me.phone || '' });
    setDirty(false);
  }, [me]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!String(form.name).trim()) { toast('A name is needed'); return; }
    setBusy(true);
    try {
      await api.auth.updateMe(form);
      setDirty(false);
      /* The name is on the account menu and every avatar, so the page is
         reloaded rather than left showing two versions of the same person
         until somebody happens to refresh. */
      toast('Saved — reloading');
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast(e.message || 'That could not be saved');
    } finally {
      setBusy(false);
    }
  };

  const who = me?.name || S.senderName || 'You';

  return (
    <>
      <Section>
        <div className="profhead">
          <div className={`who ${avatarClass(who)}`}>{initials(who)}</div>
          <div>
            <h4>{who}</h4>
            <p>
              {me?.role || 'Owner'}
              {me?.dept ? ` · ${me.dept}` : ''}
              {me?.title ? ` · ${me.title}` : ''}
            </p>
          </div>
        </div>

        {/* What the account is, as given when it was created. None of it is
            yours to change, so none of it is a box. */}
        <div className="profgrid">
          <Fact label="Signing in as">{me?.email}</Fact>
          <Fact label="Role" hint="set by whoever runs the workspace">{me?.role}</Fact>
          <Fact label="Joined">{me?.createdAt ? fmtDate(new Date(me.createdAt)) : null}</Fact>
          <Fact label="Last signed in">
            {me?.lastLoginAt ? fmtDate(new Date(me.lastLoginAt), true) : 'this session'}
          </Fact>
        </div>
      </Section>

      <Section title="Your details" hint="Only you see these change; they are on your account, not the workspace.">
        <div className="profedit">
          <label>
            <span>Full name</span>
            <input value={form.name} onChange={e => set('name', e.target.value)} />
          </label>
          <label>
            <span>Job title</span>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Sales executive" />
          </label>
          <label>
            <span>Department</span>
            <input value={form.dept} onChange={e => set('dept', e.target.value)} placeholder="Sales" />
          </label>
          <label>
            <span>Phone</span>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+971 50 123 4567" />
          </label>
        </div>

        <div className="profsave">
          <button type="button" className="btn primary" disabled={!dirty || busy} onClick={save}>
            <Icon name={busy ? 'refresh' : 'check'} />{busy ? 'Saving…' : 'Save my details'}
          </button>
          {dirty ? <small>Unsaved changes</small> : null}
        </div>
      </Section>

      {/*
        * Below here is the workspace's, not yours.
        *
        * The signature goes out under the workspace's name and the two
        * defaults decide how lists behave for everybody, so somebody who
        * does not own it is not offered them.
        */}
      {owner ? (
        <>
          <Section title="Email signature" hint="Appended when you draft outreach.">
            <TextField name="signature" area />
          </Section>

          <Section title="Workspace defaults">
            <Grid>
              <SelectField label="Time zone" name="tz" options={TIMEZONES} />
              <TextField label="Working hours" name="hours" hint="used when scheduling follow-ups" />
            </Grid>
            <CheckGrid>
              <Toggle label="Assign new records to me by default" name="assignMe" />
              <Toggle label="Show my records first in lists" name="mineFirst" />
            </CheckGrid>
          </Section>
        </>
      ) : null}
    </>
  );
}
